import { useState } from "react"
import Loader from "../UI/Loader/Loader"
import { Formik, Form, Field, FieldArray } from "formik"
import PrimaryModal from "../UI/Modal/PrimaryModal"
import { PrimaryButton } from "../UI";
import { activateTealiumExperience } from "../../utils/APIs"
import { useUserContext } from "../../contexts/UserContext"
import { useContentfulContext } from "../../contexts/ContentfulContext"
import { activateTealiumAudienceSchema } from "../../schemas"
import { toast } from "react-toastify"
import Dropdown from "../UI/Dropdown/Dropdown"
import { Plus, Trash2, X } from "lucide-react"

const CreateTealiumModal = ({ closeModal, code, experience_id }) => {
    const { content } = useContentfulContext()
    const { userData } = useUserContext()

    const [loading, setLoading] = useState(false)

    // Available variables for conditions
    const conditionVariables = [
        { name: "URL", value: "dom.url" },
    ]

    // Available operators
    const conditionOperators = [
        {
            name: content.TEALIUM_ACTIVATION_MODAL.conditions.equals,
            value: "equals",
        },
        {
            name: content.TEALIUM_ACTIVATION_MODAL.conditions.does_not_equal,
            value: "does_not_equal",
        },
        {
            name: content.TEALIUM_ACTIVATION_MODAL.conditions.contains,
            value: "contains",
        },
        {
            name: content.TEALIUM_ACTIVATION_MODAL.conditions.does_not_contain,
            value: "does_not_contain",
        },
    ];

    const handleSubmit = async (values) => {
        try {
            setLoading(true)

            const data = {
                code: typeof code === "string" ? code : JSON.stringify(code),
                experience_id,
                name: values.name,
                notes: values.notes,
                scope: values.scope,
                occurrence: values.occurrence,
                conditions: values.conditions,
                selectedTargets: {
                    qa: values.targetQA,
                    dev: values.targetDev,
                    prod: values.targetProd,
                },
                tealium_integration_id: userData?.defaultTealiumKey?._id,
            }

            const result = await activateTealiumExperience(userData.token, {
                organizationId: userData.selectedOrganization.org_token,
                data,
            })
            // for this experience offer mapping created in backend 
            toast.success(result.message)
            closeModal()
        } catch (error) {
            toast.error(error?.message || content?.COMMON_MESSAGE?.something_wrong_msg)
        } finally {
            setLoading(false)
        }
    }

    return (
        <PrimaryModal title={"Create Tealium Entry"} hideModal={closeModal}>
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                {loading && <Loader />}
                <Formik
                    initialValues={{
                        name: "",
                        notes: "",
                        scope: "",
                        occurrence: "",
                        targetQA: false,
                        targetDev: false,
                        targetProd: false,
                        conditions: [
                            [
                                {
                                    variable: "",
                                    operator: "equals",
                                    value: "",
                                },
                            ],
                        ],
                    }}
                    validationSchema={() => activateTealiumAudienceSchema(content.TEALIUM_ACTIVATION_MODAL)}
                    onSubmit={handleSubmit}
                >
                    {({ errors, touched, setFieldValue, values }) => (
                        <Form className="space-y-4" id="create-contentful-form">
                            <div>
                                <div className="relative">
                                    <Field
                                        type="text"
                                        id="name"
                                        name="name"
                                        className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                        placeholder=" "
                                    />
                                    <label
                                        htmlFor="name"
                                        className="absolute text-sm text-black duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-primary-2 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                                    >
                                        {content.TEALIUM_ACTIVATION_MODAL.form.name}
                                    </label>
                                </div>
                                {errors.name && touched.name && <p className="mt-2 text-sm text-red-500">{errors.name}</p>}
                            </div>

                            <div>
                                <div className="relative">
                                    <textarea
                                        type="textarea"
                                        id="notes"
                                        name="notes"
                                        className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border border-gray-2 appearance-none focus:outline-none focus:ring-1 focus:ring-primary-2 ring-primary peer"
                                        placeholder=" "
                                        onChange={(e) => setFieldValue("notes", e.target.value)}
                                        rows={4}
                                    />
                                    <label
                                        htmlFor="notes"
                                        className="absolute text-sm text-black duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 peer-focus:px-2 peer-focus:text-primary peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4 left-1"
                                    >
                                        {content.TEALIUM_ACTIVATION_MODAL.form.notes}
                                    </label>
                                </div>
                                {errors.notes && touched.notes && <p className="mt-2 text-sm text-red-500">{errors.notes}</p>}
                            </div>

                            <div className="text-sm flex gap-3 items-center">
                                <label className="block text-gray-700 text-sm font-bold">
                                    {content.TEALIUM_ACTIVATION_MODAL.form.scope}
                                </label>
                                <Dropdown
                                    items={content.TEALIUM_ACTIVATION_MODAL.scopes}
                                    callback={(value) => setFieldValue("scope", value.name)}
                                />
                            </div>

                            <div className="text-sm flex gap-3 items-center">
                                <label className="block text-gray-700 text-sm font-bold">
                                    {content.TEALIUM_ACTIVATION_MODAL.form.occurrence}
                                </label>
                                <Dropdown
                                    items={content.TEALIUM_ACTIVATION_MODAL.occurrences}
                                    initialValue={content.TEALIUM_ACTIVATION_MODAL.occurrences.find(
                                        (item) => item.name === content.TEALIUM_ACTIVATION_MODAL.default_occurrence,
                                    )}
                                    callback={(value) => setFieldValue("occurrence", value.name)}
                                />
                            </div>

                            <div className="text-sm flex gap-3">
                                <label className="text-gray-700 text-sm font-bold mb-1">
                                    {content.TEALIUM_ACTIVATION_MODAL.form.targets}
                                </label>
                                <div className="flex gap-2 ">
                                    <div className="flex items-center mb-4">
                                        <Field
                                            type="checkbox"
                                            name={`targetQA`}
                                            className="h-4 w-4 text-primary border-gray-300 rounded"
                                        />
                                        <label htmlFor={`targetQA`} className="ml-2 block text-sm font-medium">
                                            {content.TEALIUM_ACTIVATION_MODAL.form.qa}
                                        </label>
                                    </div>
                                    <div className="flex items-center mb-4">
                                        <Field
                                            type="checkbox"
                                            name={`targetDev`}
                                            className="h-4 w-4 text-primary border-gray-300 rounded"
                                        />
                                        <label htmlFor={`targetDev`} className="ml-2 block text-sm font-medium">
                                            {content.TEALIUM_ACTIVATION_MODAL.form.dev}
                                        </label>
                                    </div>
                                    <div className="flex items-center mb-4">
                                        <Field
                                            type="checkbox"
                                            name={`targetProd`}
                                            className="h-4 w-4 text-primary border-gray-300 rounded"
                                        />
                                        <label htmlFor={`targetProd`} className="ml-2 block text-sm font-medium">
                                            {content.TEALIUM_ACTIVATION_MODAL.form.prod}
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {/* Conditions Section */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="block text-gray-700 text-sm font-bold">{content.TEALIUM_ACTIVATION_MODAL.form.url_condition}</label>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newConditions = [...values.conditions, [{ variable: "", operator: "equals", value: "" }]]
                                            setFieldValue("conditions", newConditions)
                                        }}
                                        className="flex items-center gap-1 px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary transition-colors"
                                    >
                                        <Plus size={12} />
                                        {content.TEALIUM_ACTIVATION_MODAL.conditions.add_group}
                                    </button>
                                </div>

                                <FieldArray name="conditions">
                                    {({ remove: removeGroup }) => (
                                        <div className="space-y-4">
                                            {values.conditions.map((conditionGroup, groupIndex) => (
                                                <div key={groupIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <span className="text-sm font-medium text-gray-600">{content.TEALIUM_ACTIVATION_MODAL.conditions.group_label} {groupIndex + 1}</span>
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const newConditions = [...values.conditions]
                                                                    newConditions[groupIndex].push({ variable: "", operator: "equals", value: "" })
                                                                    setFieldValue("conditions", newConditions)
                                                                }}
                                                                className="flex items-center gap-1 px-2 py-1 text-xs bg-primary text-white rounded hover:bg-primary transition-colors"
                                                            >
                                                                <Plus size={10} />
                                                                {content.TEALIUM_ACTIVATION_MODAL.conditions.add_condition}
                                                            </button>
                                                            {values.conditions.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeGroup(groupIndex)}
                                                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                                                                >
                                                                    <Trash2 size={10} />
                                                                    {content.TEALIUM_ACTIVATION_MODAL.conditions.remove_group}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <FieldArray name={`conditions.${groupIndex}`}>
                                                        {({ remove: removeCondition }) => (
                                                            <div className="space-y-3">
                                                                {conditionGroup.map((condition, conditionIndex) => (
                                                                    <div
                                                                        key={conditionIndex}
                                                                        className="flex flex-wrap gap-2 items-center bg-white p-3 rounded border"
                                                                    >
                                                                        {/* Variable Dropdown */}
                                                                        <div className="min-w-[150px] flex-1">
                                                                            <label className="block text-xs text-gray-500 mb-1">{content.TEALIUM_ACTIVATION_MODAL.conditions.url_label}</label>
                                                                            <Field
                                                                                as="select"
                                                                                name={`conditions.${groupIndex}.${conditionIndex}.variable`}
                                                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                                            >
                                                                                <option value="">Select URL</option>
                                                                                {conditionVariables.map((variable) => (
                                                                                    <option key={variable.value} value={variable.value}>
                                                                                        {variable.name}
                                                                                    </option>
                                                                                ))}
                                                                            </Field>
                                                                        </div>

                                                                        {/* Operator Dropdown */}
                                                                        <div className="min-w-[150px] flex-1">
                                                                            <label className="block text-xs text-gray-500 mb-1">{content.TEALIUM_ACTIVATION_MODAL.conditions.operator_label}</label>
                                                                            <Field
                                                                                as="select"
                                                                                name={`conditions.${groupIndex}.${conditionIndex}.operator`}
                                                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                                            >
                                                                                {conditionOperators.map((operator) => (
                                                                                    <option key={operator.value} value={operator.value}>
                                                                                        {operator.name}
                                                                                    </option>
                                                                                ))}
                                                                            </Field>
                                                                        </div>

                                                                        {/* Value Input */}
                                                                        <div className="min-w-[150px] flex-1">
                                                                            <label className="block text-xs text-gray-500 mb-1">{content.TEALIUM_ACTIVATION_MODAL.conditions.value_label}</label>
                                                                            <Field
                                                                                type="text"
                                                                                name={`conditions.${groupIndex}.${conditionIndex}.value`}
                                                                                className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                                                                                placeholder={
                                                                                    condition.variable === "url"
                                                                                        ? "https://example.com"
                                                                                        : condition.operator?.includes("badge")
                                                                                            ? ""
                                                                                            : "Enter value"
                                                                                }
                                                                                disabled={condition.operator?.includes("badge")}
                                                                            />
                                                                        </div>

                                                                        {/* Remove Condition Button */}
                                                                        {conditionGroup.length > 1 && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeCondition(conditionIndex)}
                                                                                className="flex items-center justify-center w-8 h-8 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                                                title={content.TEALIUM_ACTIVATION_MODAL.conditions.remove_condition_title}
                                                                            >
                                                                                <X size={14} />
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                                {/* {conditionGroup.length > 1 && (
                                                                    <div className="text-xs text-gray-500 text-center">
                                                                       {content.TEALIUM_ACTIVATION_MODAL.conditions.group_and_note}
                                                                    </div>
                                                                )} */}
                                                            </div>
                                                        )}
                                                    </FieldArray>
                                                </div>
                                            ))}
                                            {/* {values.conditions.length > 1 && (
                                                <div className="text-xs text-gray-500 text-center">
                                                    {content.TEALIUM_ACTIVATION_MODAL.conditions.group_or_note}
                                                </div>
                                            )} */}
                                        </div>
                                    )}
                                </FieldArray>
                            </div>

                            <div className="!mt-0 h-[32px]">
                                {values.targetProd && (
                                    <p className="!mt-0 text-xs italic">{content.TEALIUM_ACTIVATION_MODAL.prod_warning}</p>
                                )}
                            </div>

                            <div className="flex justify-end">
                                <PrimaryButton type="submit">{content.TEALIUM_ACTIVATION_MODAL.create_btn}</PrimaryButton>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </PrimaryModal>
    )
}

export default CreateTealiumModal
